import { ImagePaths } from '../../lib/imageUrls.js';

export const ai901AzureAiFundamentals = {

    id: 19,
    slug: 'ai-901-azure-ai-fundamentals',
    ogImage: '/og/courses/ai-901-azure-ai-fundamentals.jpg',
    courseCode: 'AI-900-001',
    featured: false,
    title: 'AI-901: Azure AI Fundamentals',
    subtitle: 'Master Azure AI services and prepare for AI-900 certification',

    valueProposition: 'Build foundational knowledge of Azure AI services, responsible AI principles, and machine learning fundamentals to confidently deploy AI solutions and prepare for Microsoft AI-900 certification.',

    duration: '8 hours',
    students: 'New',
    level: 'Foundational',
    organization: 'Microsoft',
    image: ImagePaths.COURSE_1,
    shortDescription: 'AI-900-001 — Foundational Azure AI training covering machine learning, generative AI, and responsible AI for IT professionals and analysts.',

    price: {
      aud: 99,
      currency: 'AUD',
      earlyBird: 89
    },

    stripeConfig: {
    priceId: 'price_1T9bE5Ku1nyc322U44DevLpe',
    productId: 'prod_U7qvs7HXWs2zQl'
  },

    snapshot: {
      whoItsFor: [
        'IT professionals new to Azure AI services',
        'Data analysts exploring AI capabilities',
        'Business analysts evaluating AI solutions',
        'Students preparing for AI-900 certification',
        'Developers beginning their AI journey',
        'Project managers overseeing AI implementations'
      ],
      accreditation: 'Preparation for Microsoft AI-900: Azure AI Fundamentals Certification'
    },

    logistics: {
      classSize: 'Maximum 20 participants for interactive labs',
      requirements: [
        'Computer with modern web browser (Edge, Chrome, or Firefox)',
        'Active Microsoft Azure account (free tier acceptable)',
        'Reliable internet connection (minimum 10 Mbps)',
        'No prior AI or programming experience required',
        'Basic familiarity with cloud concepts helpful but not required'
      ]
    },

    overview: 'Master the fundamentals of artificial intelligence on Microsoft Azure with this comprehensive 8-hour course designed for professionals new to AI. You\'ll explore Azure AI services including Computer Vision, Natural Language Processing, and Azure OpenAI Service, while building practical understanding of machine learning concepts and responsible AI principles. Through hands-on labs and real-world scenarios, you\'ll learn to identify appropriate AI workloads, implement responsible AI practices aligned with Australian governance frameworks, and prepare confidently for the Microsoft AI-900 certification exam. Perfect for Australian organisations adopting Azure AI technologies.',

    outcomes: [
      'Identify AI workloads including anomaly detection, computer vision, natural language processing, and knowledge mining',
      'Apply fundamental machine learning principles including regression, classification, and clustering on Azure',
      'Demonstrate understanding of generative AI capabilities, large language models, and prompt engineering basics',
      'Implement responsible AI practices aligned with Microsoft\'s six pillars: Fairness, Reliability, Privacy, Inclusiveness, Transparency, and Accountability',
      'Deploy Azure AI services including Computer Vision, Language Service, and Azure OpenAI Service',
      'Evaluate appropriate AI solutions for business scenarios and use cases',
      'Apply responsible AI principles to Australian compliance requirements including the APPs',
      'Prepare comprehensively for the Microsoft AI-900 certification exam'
    ],

    audience: [
      'IT professionals exploring Azure AI capabilities',
      'Data analysts and business analysts',
      'Software developers beginning AI development',
      'Project managers overseeing AI projects',
      'Students preparing for AI-900 certification',
      'Career changers entering the AI field',
      'Business decision-makers evaluating AI technologies'
    ],

    prerequisites: [
      'Basic familiarity with computers and web browsers',
      'Understanding of basic cloud concepts (helpful but not required)',
      'No programming experience required',
      'No prior AI or machine learning knowledge required',
      'Willingness to learn new technologies'
    ],

    modules: [
      {
        title: 'AI Workloads and Considerations',
        duration: '2 hours',
        topics: [
          'Introduction to artificial intelligence concepts',
          'Identifying AI workloads: anomaly detection use cases',
          'Computer vision applications and Azure Computer Vision service',
          'Natural language processing and Language Service capabilities',
          'Knowledge mining with Azure Cognitive Search',
          'Conversational AI and Azure Bot Service',
          'Selecting appropriate AI services for business scenarios'
        ]
      },
      {
        title: 'Fundamental Principles of Machine Learning',
        duration: '2 hours',
        topics: [
          'Understanding machine learning core concepts',
          'Supervised learning: regression for predictions',
          'Supervised learning: classification for categorisation',
          'Unsupervised learning: clustering for pattern discovery',
          'Training, validation, and testing datasets',
          'Azure Machine Learning workspace and capabilities',
          'Evaluating model performance and accuracy'
        ]
      },
      {
        title: 'Features of Generative AI on Azure',
        duration: '2 hours',
        topics: [
          'Introduction to generative AI and large language models (LLMs)',
          'Understanding Azure OpenAI Service capabilities',
          'Generative AI use cases: content creation, summarisation, code generation',
          'Prompt engineering fundamentals and best practices',
          'Responsible use of generative AI models',
          'Hands-on lab: Building prompts with Azure OpenAI',
          'Understanding model capabilities and limitations'
        ]
      },
      {
        title: 'Responsible AI Principles',
        duration: '2 hours',
        topics: [
          'Microsoft\'s six pillars of Responsible AI',
          'Fairness: identifying and mitigating bias in AI systems',
          'Reliability and Safety: ensuring consistent and safe AI behaviour',
          'Privacy and Security: protecting data in AI solutions',
          'Inclusiveness: designing AI accessible to all users',
          'Transparency: understanding AI decision-making',
          'Accountability: governance and oversight of AI systems',
          'Applying responsible AI to Australian Privacy Principles and compliance'
        ]
      }
    ],

    format: {
      type: 'Live online with hands-on labs',
      includes: [
        '8 hours of instructor-led training',
        'Hands-on labs in Azure AI services',
        'Azure AI service overview and comparison guide',
        'AI-900 exam preparation materials and practice questions',
        'Responsible AI framework templates',
        'AI workload assessment checklist',
        'Certificate of completion',
        '3 months access to course materials'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'One-day intensive format (8 hours) with optional follow-up Q&A session'
    },

    // Pathway relationships (used for the learning pathways system)
    pathways: [
      'ai-foundations-professional',
      'ai-security-pathway'
    ],

    // Skills and metadata used for pathway recommendation and search
    skillsTaught: [
      'Azure Cognitive Services',
      'Computer Vision basics',
      'Natural Language Processing (NLP)',
      'Responsible AI principles',
      'Prompt engineering fundamentals'
    ],

    hasCapstone: false,

    nextCourses: [
      'ai-200-azure-ai-developer-associate',
      'ai-for-business'
    ],

    faqs: [
      {
        question: 'Is this course suitable for complete beginners to AI?',
        answer: 'Absolutely. This foundational course is designed specifically for professionals with no prior AI or machine learning experience. We start with core concepts and build progressively. The only prerequisites are basic computer skills and curiosity about AI technologies.'
      },
      {
        question: 'Do I need Azure experience to take this course?',
        answer: 'No prior Azure experience is required. While familiarity with cloud concepts is helpful, we cover everything you need to know about Azure AI services from the ground up. You\'ll need a free Azure account for hands-on labs, and we\'ll guide you through the setup.'
      },
      {
        question: 'Does this course prepare me for the Microsoft AI-900 certification exam?',
        answer: 'Yes, this course is specifically designed to align with the AI-900: Microsoft Azure AI Fundamentals certification exam objectives. You\'ll receive exam preparation materials, practice questions, and comprehensive coverage of all exam topics. While we can\'t guarantee exam success, participants report strong confidence after completing this training.'
      },
      {
        question: 'What hands-on labs are included?',
        answer: 'You\'ll complete practical labs using Azure Computer Vision, Language Service, and Azure OpenAI Service. Labs include building image classification models, analysing text sentiment, creating conversational AI experiences, and experimenting with prompt engineering. All labs use the Azure free tier, so there\'s no additional cost.'
      },
      {
        question: 'How does responsible AI relate to Australian compliance requirements?',
        answer: 'We specifically address how Microsoft\'s responsible AI principles align with Australian frameworks including the Privacy Act, Australian Privacy Principles (APPs), and emerging AI governance standards. You\'ll learn to evaluate AI solutions against Australian privacy and data protection obligations.'
      },
      {
        question: 'What is the time commitment for this course?',
        answer: 'The course is delivered as a one-day intensive (8 hours) live online session. We recommend setting aside the full day to maximise learning and hands-on practice. You\'ll also receive 3 months access to the course materials if you need to review any content.'
      },
      {
        question: 'Do you offer group rates for teams?',
        answer: 'Yes, we offer discounted group rates for organisations enrolling multiple staff members. This is particularly valuable for teams adopting Azure AI services together. Contact us for a customised quote based on your team size and requirements.'
      },
      {
        question: 'What support is available after course completion?',
        answer: 'You\'ll have 3 months access to course materials. We also offer optional post-course support packages and consulting to help implement Azure AI services in your organisation.'
      }
    ]
};
